import React, { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'

// Minimal shapes from Ollama /api/tags
// { models: [{ name: string, modified_at?: string, size?: number, digest?: string }] }

type OllamaTags = {
  models: Array<{ name: string; modified_at?: string; size?: number; digest?: string }>
}

export default function ModelsPage() {
  const [tags, setTags] = useState<OllamaTags | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pulling, setPulling] = useState(false)
  const [modelName, setModelName] = useState('llama3.2:3b')
  const [deleting, setDeleting] = useState<string | null>(null)
  const [settingDefault, setSettingDefault] = useState<string | null>(null)
  const [streamLog, setStreamLog] = useState<string>('')
  const streamAbortRef = useRef<AbortController | null>(null)
  const [defaultModel, setDefaultModel] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  const fmtBytes = (n?: number) => {
    if (!n || n <= 0) return ''
    const units = ['o','Ko','Mo','Go','To']
    let i = 0
    let v = n
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
    return `${v.toFixed(1)} ${units[i]}`
  }

  const fmtDate = (s?: string) => {
    if (!s) return ''
    try { return new Date(s).toLocaleString() } catch { return s }
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    setStatus(null)
    try {
      const [modelsRes, defaultRes] = await Promise.all([
        api.get('/ollama/models'),
        api.get('/ollama/default').catch(() => ({ data: { model: null } })),
      ])
      setTags(modelsRes.data)
      const backendModel = defaultRes?.data?.model || null
      setDefaultModel(backendModel)
      if (backendModel) {
        try { localStorage.setItem('ollama_default_model', backendModel) } catch {}
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement modèles')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    try {
      const local = localStorage.getItem('ollama_default_model')
      if (local) setDefaultModel(local)
    } catch {}
    const handler = (evt: Event) => {
      const detail = (evt as CustomEvent<string>).detail
      setDefaultModel(detail || null)
    }
    window.addEventListener('ollama-default-changed', handler as EventListener)
    return () => window.removeEventListener('ollama-default-changed', handler as EventListener)
  }, [])

  const pull = async () => {
    if (!modelName.trim()) return
    setPulling(true)
    setError(null)
    try {
      // Prefer streamed pull for UX; fallback to non-streamed if needed
      setStreamLog('')
      await pullStream(modelName)
      await load()
      alert(`Modèle téléchargé: ${modelName}`)
    } catch (e: any) {
      // Fallback try non-streamed endpoint via axios
      try {
        await api.post('/ollama/pull', { model: modelName })
        await load()
        alert(`Modèle téléchargé: ${modelName}`)
      } catch (ee: any) {
        alert(ee?.response?.data?.detail || e?.message || 'Erreur téléchargement modèle')
      }
    } finally {
      setPulling(false)
      streamAbortRef.current = null
    }
  }

  const pullStream = async (name: string) => {
    const ctrl = new AbortController()
    streamAbortRef.current = ctrl
    const token = localStorage.getItem('access_token') || ''
    const res = await fetch(`${location.origin}/api/ollama/pull_stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ model: name }),
      signal: ctrl.signal,
    })
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let acc = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      acc += decoder.decode(value, { stream: true })
      setStreamLog(acc)
    }
  }

  const del = async (name: string) => {
    if (!confirm(`Supprimer le modèle ${name} ?`)) return
    setDeleting(name)
    try {
      await api.post('/ollama/delete', { model: name })
      await load()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Erreur suppression modèle')
    } finally {
      setDeleting(null)
    }
  }

  const setDefault = async (name: string) => {
    setSettingDefault(name)
    try {
      await api.post('/ollama/default', { model: name })
      setDefaultModel(name)
      try { localStorage.setItem('ollama_default_model', name) } catch {}
      try { window.dispatchEvent(new CustomEvent('ollama-default-changed', { detail: name })) } catch {}
      setStatus(`Modèle par défaut mis à jour: ${name}`)
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Erreur définition modèle par défaut'
      setError(msg)
    } finally {
      setSettingDefault(null)
    }
  }

  const models = useMemo(() => tags?.models || [], [tags])

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {status && (
            <div className="mb-3 text-sm text-emerald-600 dark:text-emerald-300">{status}</div>
          )}
          {defaultModel && (
            <div className="mb-4 text-sm text-zinc-600 dark:text-zinc-300">
              Modèle par défaut actuel&nbsp;: <span className="font-medium">{defaultModel}</span>
            </div>
          )}

          <div className="mb-6 p-4 border rounded-lg bg-zinc-50 dark:bg-zinc-900/30 dark:border-zinc-800">
            <div className="text-sm mb-2">Télécharger un modèle</div>
            <div className="flex items-center gap-2">
              <input
                value={modelName}
                onChange={e => setModelName(e.target.value)}
                className="flex-1 border rounded px-3 py-2 bg-transparent"
                placeholder="ex: llama3.2:3b"
                disabled={pulling}
              />
              <button onClick={pull} disabled={pulling || !modelName.trim()} className="px-3 py-2 rounded bg-emerald-600 text-white disabled:opacity-50">
                {pulling ? 'Téléchargement…' : 'Télécharger (stream)'}
              </button>
            </div>
            <div className="text-xs text-zinc-500 mt-2">Les modèles sont stockés dans /var/lib/totemai/ollama/.</div>
            {pulling && (
              <pre className="mt-3 max-h-40 overflow-auto text-xs p-2 rounded bg-black/70 text-emerald-200">
                {streamLog || 'En cours…'}
              </pre>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-zinc-500">Chargement…</div>
          ) : error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse table-fixed">
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '16%' }} />
              <col />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead className="bg-emerald-50/50 border-b border-emerald-100/70 dark:bg-zinc-900/50 dark:border-emerald-900/40">
              <tr>
                <th className="py-3 pr-4 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Nom</th>
                <th className="py-3 pr-4 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Taille</th>
                <th className="py-3 pr-4 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Modifié</th>
                <th className="py-3 pr-4 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Digest</th>
                <th className="py-3 pr-4 text-right text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider sticky right-0 bg-inherit z-20 border-l border-emerald-100/70 dark:border-emerald-900/40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.length === 0 && (
                <tr><td className="py-3" colSpan={5}>Aucun modèle local.</td></tr>
              )}
              {models.map(m => {
                const isDefault = defaultModel === m.name
                return (
                  <tr key={m.name} className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{m.name}</span>
                        {isDefault && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700">
                            Défaut
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">{fmtBytes(m.size)}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(m.modified_at)}</td>
                    <td className="py-2 pr-4 text-xs text-zinc-500 break-all">{m.digest || ''}</td>
                    <td className="py-2 pr-4 sticky right-0 bg-inherit z-10 border-l border-zinc-200 dark:border-zinc-800">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setDefault(m.name)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                          disabled={settingDefault === m.name || isDefault}
                          title="Définir comme modèle par défaut (backend)"
                        >
                          {isDefault ? 'Défaut actuel' : settingDefault === m.name ? 'Définition…' : 'Définir défaut'}
                        </button>
                        <button
                          onClick={() => del(m.name)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                          disabled={!!deleting}
                        >
                          {deleting === m.name ? 'Suppression…' : 'Supprimer'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
